import { HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { HttpFetch } from '@red/common';
import * as i0 from '@angular/core';
import { LOCALE_ID, Pipe, Inject, InjectionToken, NgModule } from '@angular/core';
import * as i1 from '@angular/platform-browser';
import { User, Company, ShareholderDetails } from '@red/data';

class ErrorResponse {
    constructor(message, httpCode, errorCode, traceId) {
        this._message = message;
        this._httpCode = httpCode;
        this._errorCode = errorCode;
        this._traceId = traceId;
    }
    get errorCode() { return this._errorCode; }
    get httpCode() { return this._httpCode; }
    get message() { return this._message; }
    get traceId() { return this._traceId; }
}

var REMOTE_ERROR_EXCEPTION_TYPE;
(function (REMOTE_ERROR_EXCEPTION_TYPE) {
    REMOTE_ERROR_EXCEPTION_TYPE[REMOTE_ERROR_EXCEPTION_TYPE["RESPONSE"] = 0] = "RESPONSE";
    REMOTE_ERROR_EXCEPTION_TYPE[REMOTE_ERROR_EXCEPTION_TYPE["CANCEL"] = 1] = "CANCEL";
})(REMOTE_ERROR_EXCEPTION_TYPE || (REMOTE_ERROR_EXCEPTION_TYPE = {}));
class RemoteErrorException extends Error {
    constructor(message, errorResponse) {
        super(message);
        this._errorResponse = errorResponse;
        this._type = REMOTE_ERROR_EXCEPTION_TYPE.RESPONSE;
        Object.setPrototypeOf(this, RemoteErrorException.prototype);
    }
    static fromErrorResponse(err) {
        return new RemoteErrorException(err.message, err);
    }
    static fromHttpErrorResponse(err) {
        let message = err.message;
        let errorCode;
        let traceId;
        if (typeof err.error === 'object') {
            message = err.error.message || message;
            errorCode = err.error.errorCode || null;
            traceId = err.error.traceId || null;
        }
        const errorResponse = new ErrorResponse(message, err.status, errorCode, traceId);
        const error = RemoteErrorException.fromErrorResponse(errorResponse);
        /*
         This is a best guess effort to detect if an request was cancelled by either
         losing connection or when you leave Safari on a iOS device.
        */
        if (error.httpCode === 0 && err.error instanceof ProgressEvent) {
            error.setErrorType(REMOTE_ERROR_EXCEPTION_TYPE.CANCEL);
        }
        return error;
    }
    get errorCode() {
        return this._errorResponse.errorCode;
    }
    get httpCode() {
        return this._errorResponse.httpCode;
    }
    get traceId() {
        return this._errorResponse.traceId;
    }
    get isCancelled() {
        return this._type === REMOTE_ERROR_EXCEPTION_TYPE.CANCEL;
    }
    setErrorType(type) {
        this._type = type;
    }
}

class NgHttpFetch extends HttpFetch {
    constructor(http) {
        super();
        this._http = http;
    }
    static create(http) {
        return new NgHttpFetch(http);
    }
    request(request, options = {}) {
        if (!options['headers']) {
            options.headers = {};
        }
        if (!options['observe']) {
            options.observe = 'response';
        }
        options.headers = new HttpHeaders(options.headers);
        this._setHeaders(options);
        return this._http
            .request(request.method, request.url, Object.assign({ body: request.body }, options))
            .pipe(map((response) => {
            return {
                status: response.status,
                statusText: response.statusText,
                url: response.url,
                body: response.body,
                headers: response.headers,
            };
        }));
    }
    _setHeaders(opt) {
        if (!opt.headers.get('Content-Type')) {
            opt.headers = opt.headers.set('Content-Type', 'application/json; charset=utf-8');
        }
        if (opt.headers.get('Content-Type') === 'multipart/form-data') {
            opt.headers = opt.headers.delete('Content-Type');
        }
    }
}

class AmountPipe {
    constructor(domSanitizer, localeId) {
        this._domSanitizer = domSanitizer;
        this._localeId = localeId;
    }
    transform(amount, minimumFractionDigits, maximumFractionDigits) {
        let str = '';
        if (amount && typeof amount.amount === 'number') {
            str = amount.amount.toLocaleString(this._localeId, {
                'style': 'currency',
                'currency': amount.currency && amount.currency.toString(),
                'currencyDisplay': 'symbol',
                'useGrouping': true,
                'minimumFractionDigits': typeof minimumFractionDigits === 'number' ? minimumFractionDigits : 2,
                'maximumFractionDigits': typeof maximumFractionDigits === 'number' ? maximumFractionDigits : 2
            });
        }
        return this._domSanitizer.bypassSecurityTrustHtml(str);
    }
}
AmountPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.3", ngImport: i0, type: AmountPipe, deps: [{ token: i1.DomSanitizer }, { token: LOCALE_ID }], target: i0.ɵɵFactoryTarget.Pipe });
AmountPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.2.3", ngImport: i0, type: AmountPipe, name: "amount" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.3", ngImport: i0, type: AmountPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'amount' }]
        }], ctorParameters: function () {
        return [{ type: i1.DomSanitizer }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [LOCALE_ID]
                    }] }];
    } });

const MOMENT_DEFAULT_FORMAT = new InjectionToken('MOMENT_DEFAULT_FORMAT');
const notSameYearFormats = {
    'D MMM': `D MMM YYYY`,
    'D MMM, HH:mm': 'D MMM YYYY, HH:mm'
};
class MomentPipe {
    constructor(defaultFormat) {
        this._defaultFormat = defaultFormat;
        this._moment = window['moment'];
    }
    transform(value, format = this._defaultFormat) {
        if (this._moment) {
            const modFormat = this._addYearIfOtherThanCurrent(value, format);
            return this._moment(value).format(modFormat);
        }
        else {
            console.warn(`MomentPipe requires moment.js to be loaded as a global var window['moment'].`);
            return value;
        }
    }
    _addYearIfOtherThanCurrent(value, format) {
        return (!this._moment().isSame(value, 'year') && notSameYearFormats[format])
            ? notSameYearFormats[format]
            : format;
    }
}
MomentPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.3", ngImport: i0, type: MomentPipe, deps: [{ token: MOMENT_DEFAULT_FORMAT }], target: i0.ɵɵFactoryTarget.Pipe });
MomentPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.2.3", ngImport: i0, type: MomentPipe, name: "moment" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.3", ngImport: i0, type: MomentPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'moment' }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [MOMENT_DEFAULT_FORMAT]
                    }] }];
    } });

class ShareholderPipe {
    transform(value) {
        let name;
        if (value instanceof User) {
            name = (value.details.firstName + ' ' + value.details.lastName);
        }
        if (value instanceof Company) {
            name = value.name;
        }
        if (value instanceof ShareholderDetails) {
            name = value.name;
        }
        return name;
    }
}
ShareholderPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.3", ngImport: i0, type: ShareholderPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
ShareholderPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.2.3", ngImport: i0, type: ShareholderPipe, name: "shareholder" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.3", ngImport: i0, type: ShareholderPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'shareholder' }]
        }] });

class RedPipeModule {
}
RedPipeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.3", ngImport: i0, type: RedPipeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
RedPipeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.2.3", ngImport: i0, type: RedPipeModule, declarations: [AmountPipe,
        MomentPipe,
        ShareholderPipe], exports: [AmountPipe,
        MomentPipe,
        ShareholderPipe] });
RedPipeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.2.3", ngImport: i0, type: RedPipeModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.3", ngImport: i0, type: RedPipeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        AmountPipe,
                        MomentPipe,
                        ShareholderPipe
                    ],
                    exports: [
                        AmountPipe,
                        MomentPipe,
                        ShareholderPipe
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AmountPipe, ErrorResponse, MOMENT_DEFAULT_FORMAT, MomentPipe, NgHttpFetch, REMOTE_ERROR_EXCEPTION_TYPE, RedPipeModule, RemoteErrorException, ShareholderPipe };
